import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { useProxyData } from '../contexts/ProxyDataContext';
import { useEmailInbox } from '../contexts/EmailInboxContext';
import { useProxyWorkflowContext } from '../hooks/useProxyWorkflowContext';
import { PROXY_SEED_TODAY } from '../data/mockProxyJobs';
import { buildMaterialFollowupTask, buildProxyIntakeTasks, materialFollowupTaskFor } from '../data/proxyWorkflowRegistry';
import { resolveOutreachContact } from '../data/proxyFallbackSolicitors';
import { pullFollowUpReport, type PulledRow, type PullStepId } from '../utils/followUpReportPull';
import { BandChip, RecommendedActionChip } from '../components/proxy/ProxyChips';

// ICT-7 manual pull — when Alteryx is down, Ace pulls the Proxy Material
// Report and applies every ICT-7 step itself. The person reviews the trace,
// can put back any row Ace excluded, then sends the report to the team.
function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${m}.${d}.${y}`;
}

export function ProxyManualPullPage() {
  const { setTopbar } = useAppShell();
  const navigate = useNavigate();
  const { jobs, updateJob } = useProxyData();
  const { appendOutbound } = useEmailInbox();
  const ctx = useProxyWorkflowContext();
  const [includeAnyway, setIncludeAnyway] = useState<Set<string>>(new Set());
  const [openStep, setOpenStep] = useState<PullStepId | null>('ops_note');
  const [tab, setTab] = useState<'Red' | 'Yellow'>('Red');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setTopbar('Follow Up Report: Manual Pull', 'ICT-7 · Ace pulled the Proxy Material Report from Report Server and applied each step. Check the exclusions, then send it to the team.');
  }, [setTopbar]);

  const pull = useMemo(() => pullFollowUpReport(jobs, ctx.receipts, PROXY_SEED_TODAY, includeAnyway), [jobs, ctx.receipts, includeAnyway]);
  const tabRows = pull.rows.filter(r => r.band === tab);
  const groups = useMemo(() => {
    const map = new Map<string, PulledRow[]>();
    tabRows.forEach(r => {
      const key = r.job.solicitor || 'Issuer';
      map.set(key, [...(map.get(key) || []), r]);
    });
    return Array.from(map.entries());
  }, [tabRows]);
  const reportName = `ICT Follow Ups – ${fmtDate(PROXY_SEED_TODAY)}`;

  function toggleInclude(jobId: string) {
    setIncludeAnyway(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });
  }

  function createFollowUp(row: PulledRow) {
    const existing = materialFollowupTaskFor(buildProxyIntakeTasks(ctx), row.job.jobId);
    if (existing) { navigate('/ace', { state: { proxyTask: existing } }); return; }
    updateJob(row.job.jobId, { materialFollowupRequestedAt: PROXY_SEED_TODAY });
    navigate('/ace', { state: { proxyTask: buildMaterialFollowupTask(row.job, PROXY_SEED_TODAY) } });
  }

  function sendToTeam() {
    const red = pull.rows.filter(r => r.band === 'Red').length;
    appendOutbound({
      refKey: 'ict-follow-ups-manual-pull',
      jobIds: [],
      origin: 'proxy',
      to: 'ict@betanxt.com',
      subject: reportName,
      body: `Manual pull (Alteryx unavailable). Red Follow Ups: ${red} jobs. Yellow Follow Ups: ${pull.rows.length - red} jobs. Grouped by solicitor; contacts cross-referenced against the Solicitor Contact List.`,
    });
    setSent(true);
  }

  return (
    <div className="h-full overflow-auto px-6 py-6">
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* Ace's trace — what each ICT-7 step removed, and why */}
        <div data-tour="pull-trace" className="rounded-xl border border-trim bg-white self-start">
          <div className="px-4 py-3 border-b border-trim">
            <div className="text-sm font-semibold text-ink">What Ace did</div>
            <div className="text-xs text-muted mt-0.5">{pull.pulled} jobs pulled from the Proxy Material Report</div>
          </div>
          <ol>
            {pull.steps.map((step, i) => {
              const rows = pull.excluded.filter(e => e.step === step.id);
              const isOpen = openStep === step.id;
              return (
                <li key={step.id} data-tour={`pull-step-${step.id}`} className="border-b border-trim last:border-b-0">
                  <button
                    onClick={() => setOpenStep(isOpen ? null : step.id)}
                    disabled={!rows.length}
                    className="w-full text-left px-4 py-2.5 flex items-start gap-3"
                  >
                    <span className="w-5 h-5 rounded-full bg-wash text-xs font-semibold text-copy flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <span className="flex-1 text-xs text-ink">{step.label}<span className="text-muted"> · {step.sop}</span></span>
                    <span className={`text-xs font-semibold whitespace-nowrap ${step.removed ? 'text-ink' : 'text-muted'}`}>−{step.removed}</span>
                  </button>
                  {isOpen && rows.length > 0 && (
                    <ul className="px-4 pb-3 flex flex-col gap-1.5">
                      {rows.map(e => (
                        <li key={e.job.jobId} className="flex items-center justify-between gap-2 text-xs">
                          <span className="text-copy truncate">{e.job.issuer} <span className="text-muted">· {e.reason}</span></span>
                          {step.id !== 'duplicate' && (
                            <button onClick={() => toggleInclude(e.job.jobId)} className="text-[#2563EB] font-medium whitespace-nowrap">Include anyway</button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
            <li className="px-4 py-2.5 flex items-start gap-3 border-t border-trim">
              <span className="w-5 h-5 rounded-full bg-wash text-xs font-semibold text-copy flex items-center justify-center flex-shrink-0">{pull.steps.length + 1}</span>
              <span className="flex-1 text-xs text-ink">Split Red / Yellow, moving 15–18 days to the meeting into Red<span className="text-muted"> · ICT-7</span></span>
              <span className="text-xs font-semibold text-ink whitespace-nowrap">{pull.movedToRed} moved</span>
            </li>
            <li className="px-4 py-2.5 flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-wash text-xs font-semibold text-copy flex items-center justify-center flex-shrink-0">{pull.steps.length + 2}</span>
              <span className="flex-1 text-xs text-ink">Group by solicitor and add contacts from the Solicitor Contact List<span className="text-muted"> · ICT-7</span></span>
            </li>
          </ol>
          {includeAnyway.size > 0 && (
            <div className="px-4 py-2.5 border-t border-trim text-xs text-copy flex items-center justify-between">
              <span>{includeAnyway.size} job{includeAnyway.size === 1 ? '' : 's'} put back by you</span>
              <button onClick={() => setIncludeAnyway(new Set())} className="text-[#2563EB] font-medium">Undo</button>
            </div>
          )}
        </div>

        {/* The report itself */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div data-tour="pull-tabs" className="flex items-center gap-2">
              {(['Red', 'Yellow'] as const).map(b => (
                <button
                  key={b}
                  onClick={() => setTab(b)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${tab === b ? 'bg-[#063F5A] text-white border-[#063F5A]' : 'bg-white text-copy border-trim'}`}
                >
                  {b} Follow Ups · {pull.rows.filter(r => r.band === b).length}
                </button>
              ))}
            </div>
            {sent ? (
              <span className="text-xs text-[#0F6E56] font-medium">"{reportName}" emailed to ict@betanxt.com</span>
            ) : (
              <button data-tour="pull-send" onClick={sendToTeam} className="text-xs font-semibold text-white bg-[#063F5A] rounded-lg px-3 py-2">
                Email "{reportName}" to the team
              </button>
            )}
          </div>

          {groups.map(([solicitor, rows]) => {
            const contact = resolveOutreachContact(rows[0].job);
            return (
              <div key={solicitor} data-tour={`pull-group-${solicitor}`} className="rounded-xl border border-trim bg-white mb-3">
                <div className="px-4 py-2.5 border-b border-trim flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-ink">{solicitor}</span>
                  {solicitor !== 'Issuer' && contact
                    ? <span className="text-xs text-muted">{contact.email}</span>
                    : solicitor !== 'Issuer' && <span className="tp ra-cancel">No contact on the list</span>}
                </div>
                <table className="w-full text-sm border-collapse">
                  <tbody>
                    {rows.map(row => (
                      <tr key={row.job.jobId} className="border-b border-trim last:border-b-0">
                        <td className="px-4 py-2">
                          <div className="text-sm text-ink">{row.job.issuer}</div>
                          <div className="text-xs text-muted">
                            Job {row.job.jobId} · {row.daysToMeeting}d to meeting{row.movedToRed ? ' · moved to Red' : ''}{includeAnyway.has(row.job.jobId) ? ' · put back by you' : ''}
                          </div>
                        </td>
                        <td className="px-2 py-2"><BandChip band={row.job.band} /></td>
                        <td className="px-2 py-2"><RecommendedActionChip decision={row.job.decision} /></td>
                        <td className="px-2 py-2 text-xs">
                          {row.hasContact ? null : <span className="tp ra-cancel">Contact tracing needed</span>}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button onClick={() => createFollowUp(row)} className="text-xs font-medium text-[#2563EB] whitespace-nowrap">Create follow-up</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
